import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency, getFocoScoreZone, getFocoScoreLabel } from '@/lib/foco-calculations';
import { UserAccessManager } from './UserAccessManager';
import { UserPremiumManager } from './UserPremiumManager';
import { AdminAccessLinkPanel } from './AdminAccessLinkPanel';
import { AdminDismissUserPanel } from './AdminDismissUserPanel';
import { ClientTaskManager } from './ClientTaskManager';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { CheckCircle, AlertTriangle, Plus, Trash2, ListTodo } from 'lucide-react';
import { toast } from 'sonner';
import type { ClientWithTrafficLight } from './ClientTrafficLightTable';

interface ClientDetailDialogProps {
  client: ClientWithTrafficLight | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

interface Alert {
  id: string;
  alert_type: string;
  title: string;
  description: string | null;
  severity: string;
  is_resolved: boolean;
  created_at: string;
}

interface Note {
  id: string;
  content: string;
  is_private: boolean;
  created_at: string;
}

interface FocoProgress {
  module_name: string;
  item_name: string;
  is_completed: boolean;
  completed_at: string | null;
}

export function ClientDetailDialog({ client, open, onOpenChange, onUpdate }: ClientDetailDialogProps) {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [focoProgress, setFocoProgress] = useState<FocoProgress[]>([]);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [newNote, setNewNote] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(false);

  useEffect(() => {
    if (client && open) {
      fetchClientData();
    }
  }, [client, open]);

  const fetchClientData = async () => {
    if (!client) return;
    setIsLoadingData(true);

    try {
      // Fetch alerts
      const { data: alertData } = await supabase
        .from('client_alerts')
        .select('*')
        .eq('user_id', client.id)
        .order('created_at', { ascending: false });
      setAlerts(alertData || []);

      // Fetch pending tasks count
      const { count: tasksCount } = await supabase
        .from('client_tasks')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id)
        .neq('status', 'completed');
      setPendingTasksCount(tasksCount || 0);

      // Fetch notes
      const { data: noteData } = await supabase
        .from('coach_notes')
        .select('*')
        .eq('client_id', client.id)
        .order('created_at', { ascending: false });
      setNotes(noteData || []);

      // Fetch FOCO progress
      const { data: progressData } = await supabase
        .from('foco_progress')
        .select(`
          is_completed,
          completed_at,
          module_item_id
        `)
        .eq('user_id', client.id);

      if (progressData && progressData.length > 0) {
        const itemIds = progressData.map(p => p.module_item_id);
        const { data: itemsData } = await supabase
          .from('foco_module_items')
          .select('id, name, module_id')
          .in('id', itemIds);

        if (itemsData) {
          const moduleIds = [...new Set(itemsData.map(i => i.module_id))];
          const { data: modulesData } = await supabase
            .from('foco_modules')
            .select('id, name')
            .in('id', moduleIds);

          const progress = progressData.map(p => {
            const item = itemsData.find(i => i.id === p.module_item_id);
            const module = modulesData?.find(m => m.id === item?.module_id);
            return {
              module_name: module?.name || '',
              item_name: item?.name || '',
              is_completed: p.is_completed,
              completed_at: p.completed_at,
            };
          });
          setFocoProgress(progress);
        }
      } else {
        setFocoProgress([]);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const addNote = async () => {
    if (!client || !user || !newNote.trim()) return;

    const { error } = await supabase.from('coach_notes').insert({
      client_id: client.id,
      author_id: user.id,
      content: newNote.trim(),
      is_private: false,
    });

    if (error) {
      toast.error('Error al agregar nota');
    } else {
      toast.success('Nota agregada');
      setNewNote('');
      fetchClientData();
    }
  };

  const deleteNote = async (noteId: string) => {
    const { error } = await supabase.from('coach_notes').delete().eq('id', noteId);
    if (error) {
      toast.error('Error al eliminar nota');
    } else {
      toast.success('Nota eliminada');
      fetchClientData();
    }
  };

  const resolveAlert = async (alertId: string) => {
    const { error } = await supabase
      .from('client_alerts')
      .update({ is_resolved: true, resolved_at: new Date().toISOString(), resolved_by: user?.id })
      .eq('id', alertId);

    if (error) {
      toast.error('Error al resolver alerta');
    } else {
      toast.success('Alerta resuelta');
      fetchClientData();
      onUpdate();
    }
  };

  if (!client) return null;

  const summary = client.latestSummary;
  const focoZone = summary ? getFocoScoreZone(summary.foco_score) : 'critical';

  const alertTypeLabels: Record<string, string> = {
    NO_ACTIVITY_7D: 'Sin actividad',
    VARIABLE_TOO_HIGH: 'Gastos variables altos',
    SAVINGS_TOO_LOW: 'Ahorro bajo',
    DEBT_RATIO_HIGH: 'Deuda alta',
    CATEGORY_OVER_CAP: 'Presupuesto excedido',
    INCOME_DROP_YOY: 'Caída de ingresos',
    VARIABLE_UP_YOY: 'Gastos subiendo',
    SAVINGS_DOWN_YOY: 'Ahorro bajando',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-4">
            <span>{client.name}</span>
            {summary && (
              <Badge
                variant="outline"
                className={`${
                  focoZone === 'liberty'
                    ? 'border-primary text-primary'
                    : focoZone === 'advance'
                    ? 'border-success text-success'
                    : focoZone === 'risk'
                    ? 'border-warning text-warning'
                    : 'border-destructive text-destructive'
                }`}
              >
                FOCO {summary.foco_score} - {getFocoScoreLabel(focoZone)}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="summary">Resumen</TabsTrigger>
            <TabsTrigger value="alerts">
              Alertas {alerts.filter(a => !a.is_resolved).length > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {alerts.filter(a => !a.is_resolved).length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tasks">
              Tareas {pendingTasksCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {pendingTasksCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="foco">FOCO</TabsTrigger>
            <TabsTrigger value="notes">Notas</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[60vh] mt-4">
            {/* Summary Tab */}
            <TabsContent value="summary" className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Ingresos</p>
                    <p className="text-xl font-bold">
                      {summary ? formatCurrency(summary.total_income) : '-'}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Tasa Ahorro</p>
                    <p className="text-xl font-bold">
                      {summary ? `${(summary.savings_rate * 100).toFixed(0)}%` : '-'}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Ratio Deuda</p>
                    <p className="text-xl font-bold">
                      {summary ? `${(summary.debt_ratio * 100).toFixed(0)}%` : '-'}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Flujo Real</p>
                    <p className="text-xl font-bold">
                      {summary ? formatCurrency(summary.real_cash_flow) : '-'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Control de acceso</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <UserAccessManager
                    userId={client.id}
                    userName={client.name}
                    userEmail={client.email}
                    isActive={client.is_active}
                    suspendedUntil={client.suspended_until}
                    accessExpiresAt={client.access_expires_at}
                    suspensionReason={client.suspension_reason}
                    onUpdate={onUpdate}
                  />
                  <AdminAccessLinkPanel userId={client.id} userEmail={client.email} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Suscripción Premium</CardTitle>
                </CardHeader>
                <CardContent>
                  <UserPremiumManager
                    userId={client.id}
                    userName={client.name}
                    userEmail={client.email}
                    subscriptionTier={client.subscription_tier}
                    subscriptionExpiresAt={client.subscription_expires_at}
                    onUpdate={onUpdate}
                  />
                </CardContent>
              </Card>

              <AdminDismissUserPanel
                userId={client.id}
                userName={client.name}
                userEmail={client.email}
                hasStripeSubscription={Boolean(client.stripe_subscription_id)}
                onDismissed={() => {
                  onOpenChange(false);
                  onUpdate();
                }}
              />
            </TabsContent>

            {/* Alerts Tab */}
            <TabsContent value="alerts" className="space-y-4">
              {alerts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Sin alertas</p>
              ) : (
                alerts.map((alert) => (
                  <Card key={alert.id} className={alert.is_resolved ? 'opacity-60' : ''}>
                    <CardContent className="pt-4 flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {alert.is_resolved ? (
                            <CheckCircle className="h-4 w-4 text-success" />
                          ) : (
                            <AlertTriangle className={`h-4 w-4 ${
                              alert.severity === 'critical' ? 'text-destructive' : 'text-warning'
                            }`} />
                          )}
                          <span className="font-medium">{alert.title}</span>
                          <Badge variant="outline" className="text-xs">
                            {alertTypeLabels[alert.alert_type] || alert.alert_type}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{alert.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(alert.created_at).toLocaleDateString('es')}
                        </p>
                      </div>
                      {!alert.is_resolved && (
                        <Button size="sm" variant="outline" onClick={() => resolveAlert(alert.id)}>
                          Resolver
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* Tasks Tab */}
            <TabsContent value="tasks" className="space-y-4">
              <ClientTaskManager 
                clientId={client.id} 
                onUpdate={() => {
                  fetchClientData();
                  onUpdate();
                }} 
              />
            </TabsContent>

            {/* FOCO Progress Tab */}
            <TabsContent value="foco" className="space-y-4">
              {focoProgress.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Sin progreso registrado en módulos FOCO
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Módulo</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead>Completado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {focoProgress.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.module_name}</TableCell>
                        <TableCell>{item.item_name}</TableCell>
                        <TableCell className="text-center">
                          {item.is_completed ? (
                            <CheckCircle className="h-4 w-4 text-success inline" />
                          ) : (
                            <span className="text-muted-foreground">Pendiente</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.completed_at
                            ? new Date(item.completed_at).toLocaleDateString('es')
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="space-y-4">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Agregar nota sobre el cliente..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={addNote} disabled={!newNote.trim()}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {notes.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Sin notas</p>
              ) : (
                notes.map((note) => (
                  <Card key={note.id}>
                    <CardContent className="pt-4 flex items-start justify-between">
                      <div>
                        <p className="text-sm">{note.content}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(note.created_at).toLocaleDateString('es')}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteNote(note.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
